import type { DateFormatType, SupportedLocale, TimeFormatType } from '../i18n/config'

/**
 * Known notification event types.
 *
 * Must stay in sync with the backend KnownEvents array in NotificationPreferences.cs.
 * normalizeNotificationPreferences() seeds any missing keys with the canonical defaults.
 */
export type NotificationEventType =
  | 'task.assigned'
  | 'task.mentioned'
  | 'task.commented'
  | 'task.status_changed'
  | 'task.deleted'
  | 'page.mentioned'
  | 'page.commented'
  | 'reminder.due'

/**
 * Notification event group key (UI sectioning).
 */
export type NotificationEventGroup = 'tasks' | 'pages' | 'reminders'

/**
 * UI-side metadata for each event row. Labels are the i18n key — the UI passes
 * each through `t()` before rendering. `applicableInPersonal=false` rows are
 * hidden when the active workspace is a Personal (single-user) workspace,
 * because mention/assigned/status_changed/etc. require multiple actors.
 */
export interface NotificationEventDescriptor {
  key: NotificationEventType
  group: NotificationEventGroup
  /** i18n key resolved via useTranslation in the UI. */
  labelKey: string
  /** False = hidden in Personal workspaces. */
  applicableInPersonal: boolean
}

export const NOTIFICATION_EVENT_TYPES: Array<NotificationEventDescriptor> = [
  { key: 'task.assigned',       group: 'tasks',     labelKey: 'notificationsPage.events.task_assigned',       applicableInPersonal: false },
  { key: 'task.mentioned',      group: 'tasks',     labelKey: 'notificationsPage.events.task_mentioned',      applicableInPersonal: false },
  { key: 'task.commented',      group: 'tasks',     labelKey: 'notificationsPage.events.task_commented',      applicableInPersonal: false },
  { key: 'task.status_changed', group: 'tasks',     labelKey: 'notificationsPage.events.task_status_changed', applicableInPersonal: false },
  { key: 'task.deleted',        group: 'tasks',     labelKey: 'notificationsPage.events.task_deleted',        applicableInPersonal: true },
  { key: 'page.mentioned',      group: 'pages',     labelKey: 'notificationsPage.events.page_mentioned',      applicableInPersonal: false },
  { key: 'page.commented',      group: 'pages',     labelKey: 'notificationsPage.events.page_commented',      applicableInPersonal: false },
  { key: 'reminder.due',        group: 'reminders', labelKey: 'notificationsPage.events.reminder_due',        applicableInPersonal: true },
]

/**
 * Per-event notification channel preference. Mirrors EventChannelPreference in the backend.
 * `app` is intentionally not rendered as a UI column yet; data shape preserved.
 */
export interface EventChannelPreference {
  app: boolean
  email: boolean
  push: boolean
  digest: boolean
}

/**
 * Digest email settings. `enabled` is the master switch (paired with the
 * schedule UI); per-event `digest` checkboxes are AND-gated against it.
 */
export interface DigestSettings {
  enabled: boolean
  time: string // HH:mm
  frequency: 'weekdays' | 'daily'
  /** Server-managed; clients should not set this. */
  lastDigestSentAt?: string | null
}

/**
 * Well-known dismissed-notice keys. Banners check `dismissedNotices[key]` to
 * suppress one-time prompts after the user acknowledges them.
 */
export const NOTIFICATION_NOTICE_KEYS = {
  ReminderChannelMigration: 'reminder_channel_migration',
} as const

export type NotificationNoticeKey = (typeof NOTIFICATION_NOTICE_KEYS)[keyof typeof NOTIFICATION_NOTICE_KEYS]

/**
 * User notification preferences for controlling how notifications are delivered.
 *
 * Email and Push global gates were intentionally removed — per-event toggles
 * are now the sole authority. App stays as a master gate because it has no
 * per-event UI column yet.
 */
export interface NotificationPreferences {
  enableAppNotifications: boolean
  eventPreferences?: Partial<Record<NotificationEventType, EventChannelPreference>>
  digest?: DigestSettings
  /** Notice key → ISO-8601 UTC timestamp of dismissal. */
  dismissedNotices?: Record<string, string>
}

/**
 * Canonical default channel preferences for an event. Matches the backend
 * EventChannelPreference.DefaultFor: App=true everywhere, Email/Push=false,
 * Digest=true except for time-sensitive events like reminder.due.
 */
export const defaultEventChannelPreference = (
  event?: NotificationEventType,
): EventChannelPreference => ({
  app: true,
  email: false,
  push: false,
  digest: event === 'reminder.due' ? false : true,
})

export const defaultDigestSettings: DigestSettings = {
  enabled: false,
  time: '09:00',
  frequency: 'weekdays',
}

/**
 * Default notification preferences. Use `normalizeNotificationPreferences(undefined)`
 * to get the fully-seeded shape including every known event.
 */
export const defaultNotificationPreferences: NotificationPreferences = {
  enableAppNotifications: true,
}

/**
 * Pure normalization that mirrors the backend NotificationPreferences.Normalize().
 *
 * Display-only — never auto-write the canonical shape back to RxDB. Persistence
 * happens only via explicit user toggles through useUpdateUserPreferences()
 * and via sync pull responses where the backend has already normalized.
 *
 * - Seeds every known event with canonical defaults if missing
 * - Preserves any unknown event keys as-is (forward-compat)
 * - Preserves all dismissedNotices entries verbatim (forward-compat)
 * - Drops removed legacy fields silently if they appear in the input
 */
export function normalizeNotificationPreferences(
  input: Partial<NotificationPreferences> | null | undefined,
): NotificationPreferences {
  const base: Partial<NotificationPreferences> = input ?? {}
  const eventPreferences: Record<string, EventChannelPreference> = {
    ...((base.eventPreferences ?? {}) as Record<string, EventChannelPreference>),
  }

  for (const descriptor of NOTIFICATION_EVENT_TYPES) {
    if (!eventPreferences[descriptor.key]) {
      eventPreferences[descriptor.key] = defaultEventChannelPreference(descriptor.key)
    }
  }

  return {
    enableAppNotifications: base.enableAppNotifications ?? true,
    eventPreferences: eventPreferences as Partial<
      Record<NotificationEventType, EventChannelPreference>
    >,
    digest: base.digest ?? { ...defaultDigestSettings },
    dismissedNotices: { ...(base.dismissedNotices ?? {}) },
  }
}

/**
 * Parse the JSON column with tolerance for legacy fields (enableEmailNotifications,
 * enablePushNotifications, enableSmsNotifications) that may still appear in older
 * rows. Returns a normalized struct; never throws.
 */
export function parseNotificationPreferences(
  json: string | null | undefined,
): NotificationPreferences {
  if (!json) return normalizeNotificationPreferences(undefined)
  try {
    const parsed = JSON.parse(json) as Partial<NotificationPreferences>
    return normalizeNotificationPreferences(parsed)
  } catch {
    return normalizeNotificationPreferences(undefined)
  }
}

/**
 * User preferences interface
 */
export interface UserPreferences {
  language: SupportedLocale
  dateFormat: DateFormatType
  timeFormat: TimeFormatType
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6
  theme: 'light' | 'dark' | 'system'
  timezone: string
}

/**
 * Default user preferences
 */
export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  language: 'en-US',
  dateFormat: 'MM/dd/yyyy',
  timeFormat: '12h',
  weekStartsOn: 0,
  theme: 'system',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
}

/**
 * Input type for updating preferences (all fields optional)
 */
export type UpdateUserPreferencesInput = Partial<UserPreferences>
